import useUpdateProject from '@/api/mutations/project/useUpdateProject';
import useGetMyAccountUsersIncludeDeactivated from '@/api/queries/me/useGetMyAccountUsersIncludeDeactivated';
import projectKeys from '@/api/queries/project/keyFactory';
import { ProjectByIdQuery } from '@/gql/graphql';
import { Combobox, SelectSizeVariant } from '@payaca/ambrosia';
import { DealsPermissions } from '@payaca/permissions/deals/deals.permissions';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import useGetProjectByIdOverview from '../../../../../api/queries/project/useGetProjectByIdOverview';

const ProjectOwnerControl: FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();
  const { project } = useGetProjectByIdOverview(projectId);
  const { data: accountUsers } = useGetMyAccountUsersIncludeDeactivated({
    hasAnyPermission: [DealsPermissions.GET_MY_DEAL],
  });
  const { mutateAsync: updateProjectMutation } = useUpdateProject({
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: projectKeys.overview(projectId),
      });

      // Snapshot the previous value
      const previousProjectOver = queryClient.getQueryData(
        projectKeys.overview(projectId)
      );

      // Optimistically update to the new value
      queryClient.setQueryData<ProjectByIdQuery>(
        projectKeys.overview(projectId),
        (old) => {
          if (!old) return;

          const newSelectedAccountUser = accountUsers?.find(
            (accountUser) => accountUser.id === variables.ownerId
          );

          if (!newSelectedAccountUser) {
            // Should never get here
            return old;
          }

          return {
            ...old,
            projectById: {
              ...old.projectById,
              owner: newSelectedAccountUser,
            },
          };
        }
      );

      // Return a context object with the snapshotted value
      return { previousProjectOver };
    },
    onError: (err, newTodo, context) => {
      // If an error happens, rollback!
      queryClient.setQueryData(
        projectKeys.overview(projectId),
        // @ts-expect-error
        context.previousProjectOver
      );
    },
    // Always refetch after error or success:
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: projectKeys.project(projectId),
      });
    },
  });

  const selectedOwnerId = useMemo(() => {
    if (!project?.owner) return;

    return project.owner.id;
  }, [project]);

  const availableProjectOwners = useMemo(() => {
    if (!accountUsers) return [];

    const _availableProjectOwners = accountUsers
      .filter((user) => !user.deactivatedAt)
      .map((user) => ({
        value: user.id,
        label: user.fullName,
      }));

    _availableProjectOwners.push({ value: 'UNASSIGNED', label: 'Unassigned' });

    return _availableProjectOwners;
  }, [accountUsers]);

  const handleChange = useCallback(
    async (value: string | string[]) => {
      if (Array.isArray(value)) {
        // Should never get here
        return;
      }

      await updateProjectMutation({
        projectId: projectId,
        ownerId: value === 'UNASSIGNED' ? null : value,
      });
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'projectId'
    [updateProjectMutation]
  );

  return (
    <Combobox
      CustomSelected={({ selectedOptions }) =>
        selectedOptions[0] ? (
          <>
            <b>Owner:</b> {selectedOptions[0]?.label}
          </>
        ) : null
      }
      sizeVariant={SelectSizeVariant.SM}
      value={selectedOwnerId}
      options={availableProjectOwners}
      onChange={handleChange}
    />
  );
};

export default ProjectOwnerControl;
